import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Link2, Unlink, RefreshCw, BarChart3, Send } from "lucide-react";
import { FIRM } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [items, setItems] = useState([]);
  const [cal, setCal] = useState({ configured: false, connected: false, account: null });
  const [traffic, setTraffic] = useState(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, t] = await Promise.all([
        axios.get(`${API}/consultations`),
        axios.get(`${API}/calendar/status`),
        axios.get(`${API}/traffic/summary`),
      ]);
      setItems(c.data);
      setCal(s.data);
      setTraffic(t.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/traffic/send-daily?date=${traffic?.date || ""}`);
      if (data.sent) toast.success("Traffic report emailed to the office.");
      else toast.error("Email not sent — email is not configured yet.");
    } catch {
      toast.error("Could not send report.");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    load();
    if (new URLSearchParams(window.location.search).get("calendar") === "connected") {
      toast.success("Google Calendar connected");
    }
  }, []);

  const connect = async () => {
    try {
      const { data } = await axios.get(`${API}/oauth/calendar/login`);
      window.location.href = data.authorization_url;
    } catch {
      toast.error("Google credentials not configured yet.");
    }
  };

  const disconnect = async () => {
    await axios.post(`${API}/calendar/disconnect`);
    toast.success("Disconnected");
    load();
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-page">
      <header className="bg-[#0A1128] text-white">
        <div className="max-w-[1200px] mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="overline text-[#D4AF37] mb-1">Admin</div>
            <h1 className="font-serif-display text-3xl">{FIRM.name}</h1>
          </div>
          <button data-testid="admin-refresh" onClick={load}
            className="inline-flex items-center gap-2 border border-white/25 px-4 py-2 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 py-10 space-y-10">
        <section className="bg-white border border-[#E5E7EB] p-6" data-testid="traffic-panel">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="text-[#B8962E]" />
              <div>
                <div className="font-serif-display text-xl text-[#0A1128]">Website Traffic</div>
                <p className="font-body text-sm text-[#6b7280]">
                  Today ({traffic?.date || "—"}). A full report is emailed to {FIRM.email} every morning at 8:00 AM IST.
                </p>
              </div>
            </div>
            <button data-testid="send-report-btn" onClick={sendReport} disabled={sending}
              className="inline-flex items-center gap-2 border border-[#E5E7EB] px-4 py-2 text-sm hover:border-[#0A1128] hover:text-[#0A1128] transition-colors disabled:opacity-60">
              <Send size={15} /> {sending ? "Sending…" : "Email report now"}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="border border-[#E5E7EB] p-4">
              <div className="font-serif-display text-3xl text-[#0A1128]" data-testid="traffic-views">{traffic?.views ?? 0}</div>
              <div className="overline text-[#6b7280] mt-1">Views today</div>
            </div>
            <div className="border border-[#E5E7EB] p-4">
              <div className="font-serif-display text-3xl text-[#0A1128]" data-testid="traffic-visitors">{traffic?.visitors ?? 0}</div>
              <div className="overline text-[#6b7280] mt-1">Visitors</div>
            </div>
            <div className="col-span-2 border border-[#E5E7EB] p-4">
              <div className="overline text-[#6b7280] mb-2">Top pages</div>
              {(traffic?.top_paths || []).length === 0
                ? <p className="text-sm text-[#9ca3af]">No visits yet.</p>
                : (traffic.top_paths || []).slice(0, 4).map(([p, n]) => (
                    <div key={p} className="flex justify-between text-sm text-[#4b5563]"><span>{p}</span><span className="font-medium text-[#0A1128]">{n}</span></div>
                  ))}
            </div>
          </div>
        </section>

        <section className="bg-white border border-[#E5E7EB] p-6" data-testid="calendar-panel">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="text-[#B8962E]" />
              <div>
                <div className="font-serif-display text-xl text-[#0A1128]">Google Calendar</div>
                <p className="font-body text-sm text-[#6b7280]">
                  {!cal.configured
                    ? "Not configured — add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to activate."
                    : cal.connected
                    ? `Connected${cal.account ? ` · ${cal.account}` : ""}`
                    : "Configured — connect your calendar to auto-add bookings."}
                </p>
              </div>
            </div>
            {cal.configured && (cal.connected ? (
              <button data-testid="calendar-disconnect-btn" onClick={disconnect}
                className="inline-flex items-center gap-2 border border-[#E5E7EB] px-4 py-2 text-sm hover:border-red-400 hover:text-red-500 transition-colors">
                <Unlink size={15} /> Disconnect
              </button>
            ) : (
              <button data-testid="calendar-connect-btn" onClick={connect}
                className="inline-flex items-center gap-2 bg-[#0A1128] text-white px-5 py-2.5 text-sm hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors">
                <Link2 size={15} /> Connect Calendar
              </button>
            ))}
          </div>
        </section>

        <section data-testid="consultations-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-2xl text-[#0A1128]">Consultation Requests</h2>
            <span className="font-mono-meta text-sm text-[#6b7280]">{items.length} total</span>
          </div>
          <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
            {loading ? (
              <p className="p-8 font-body text-sm text-[#6b7280]">Loading…</p>
            ) : items.length === 0 ? (
              <p className="p-8 font-body text-sm text-[#6b7280]" data-testid="no-consultations">No requests yet.</p>
            ) : (
              <table className="w-full text-left font-body text-sm">
                <thead className="bg-[#F8F9FA] border-b border-[#E5E7EB] text-[#6b7280]">
                  <tr>
                    <th className="p-4 font-medium">Client</th>
                    <th className="p-4 font-medium">Contact</th>
                    <th className="p-4 font-medium">When</th>
                    <th className="p-4 font-medium">Matter</th>
                    <th className="p-4 font-medium">Cal.</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b border-[#E5E7EB] last:border-0" data-testid={`consultation-row-${it.id}`}>
                      <td className="p-4 font-medium text-[#0A1128]">{it.name}</td>
                      <td className="p-4 text-[#4b5563]">{it.phone}<br /><span className="text-xs text-[#6b7280]">{it.email}</span></td>
                      <td className="p-4 text-[#4b5563]">{it.date}<br /><span className="text-xs text-[#6b7280]">{it.time}</span></td>
                      <td className="p-4 text-[#4b5563]">{it.subject}{it.message ? <><br /><span className="text-xs text-[#6b7280]">{it.message}</span></> : null}</td>
                      <td className="p-4">{it.calendar_event_id ? <span className="text-green-600">✓</span> : <span className="text-[#9ca3af]">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
